import { WorkflowStatus } from "@/enums/workflows";
import { WebhookEventType } from "@/enums/settings";
import { GreetingMode } from "@/types/campaigns";

export type BaseWorkflow = {
  id: string;
  name: string;
  description: string;
  status?: WorkflowStatus;
  revision?: number;
  lastModified?: string;
  /** Set when this draft was instantiated from a workflow template. */
  sourceTemplateId?: string | null;
};

export type WorkflowsListParams = {
  search?: string;
  status?: WorkflowStatus[];
  sortBy?: keyof Pick<BaseWorkflow, "name" | "status" | "lastModified">;
  sortDirection?: "asc" | "desc";
  pageSize?: number;
  cursor?: string;
};

export type PagedWorkflowsResponse = {
  data: BaseWorkflow[];
  nextCursor: string | null;
  hasMore: boolean;
};

export type Workflow = BaseWorkflow & {
  graph: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  };
};

export type DraftWorkflow = {
  name: string;
  description: string;
  graph: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  };
};

// ============================================================================
// Workflow Template Types
// ============================================================================

type WorkflowTemplateStatus = "Active" | "Archived";

/** One curated input a customer should supply after instantiating a template. */
export type TemplateSetupField = {
  nodeId: string;
  parameterPath: string;
  label?: string | null;
  helpText?: string | null;
};

export type WorkflowTemplate = {
  id: string;
  name: string;
  description: string;
  graph: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  };
  setup: TemplateSetupField[];
  status: WorkflowTemplateStatus;
  sourceWorkflowId: string | null;
  tags: string[];
  lastModified: string;
};

export type WorkflowEdge = {
  id: string;
  from: string;
  to: string;
};

// Base node properties shared by all nodes
type BaseNode = {
  id: string;
  name: string;
  displayInfo: {
    position: {
      x: number;
      y: number;
    };
    label?: string;
  };
};

// Parameter value types for variable references
// Literal: a hardcoded value
type OmegaParameterLiteral<T> = {
  type: "literal";
  value: T;
};

// Reference: a reference to another node's output (CEL expression)
type OmegaParameterReference = {
  type: "cel";
  value: string; // e.g., "node.abc123.data"
};

// Template: string interpolation with variables in double curly braces
type OmegaParameterTemplate = {
  type: "template";
  value: string; // e.g., "Hello {{ node.abc123.name }}"
};

// Union type for parameter values that can be literal, reference, or template
export type OmegaParameter<T> = OmegaParameterLiteral<T> | OmegaParameterReference | OmegaParameterTemplate;

// A list of values: either an array of individually-resolvable parameters, or a single CEL expression
export type OmegaValueList<T> = OmegaParameter<T>[] | OmegaParameterReference;

// Schedule overlap policy for cron-triggered workflows
export enum ScheduleOverlapPolicy {
  Skip = "skip",
  BufferOne = "bufferOne",
  BufferAll = "bufferAll",
  CancelOther = "cancelOther",
  TerminateOther = "terminateOther",
}

// Schedule configuration for automatic workflow execution
export type ScheduleConfig = {
  cronExpression: string;
  timezone?: string;
  overlap?: ScheduleOverlapPolicy;
};

/** Omega's `WebHookSecretLocation`. Carrier only — the value is compared the same way. */
export enum WebhookSecretLocation {
  Query = "query",
  Header = "header",
}

/**
 * The optional shared secret on the anonymous hook route (GAIL-993). A request without it
 * gets the same 404 an unknown workflow does. `in` and `name` are optional because Omega's
 * record defaults them — read them through `resolveWebhookSecret`.
 */
export type WebhookSecretConfig = {
  value: string;
  in?: WebhookSecretLocation;
  name?: string;
};

// Webhook configuration for workflow triggering via HTTP
export type WebhookConfig = {
  isEnabled: boolean;
  /** Survives `isEnabled: false` — turning the hook off must not silently drop the gate. */
  secret?: WebhookSecretConfig;
};

/**
 * The placeholders in {@link GoogleFormScript.script}, carried on the wire so the builder
 * substitutes by lookup — a rename in Omega must not ship an unsubstituted script.
 */
export type GoogleFormScriptTokens = {
  hookUrl: string;
  secretHeaderName: string;
  secretHeaderValue: string;
};

/** `GET /nodes/start/webhook/google-form-script` — the Apps Script body, tokens unsubstituted. */
export type GoogleFormScript = {
  script: string;
  tokens: GoogleFormScriptTokens;
};

export type StartNodeMetadata = {
  toolId?: string;
  exposeAsTool?: boolean;
  toolStartMode?: WorkflowToolStartMode;
  acceptFiles?: boolean;
  gailWebhookEvents?: WebhookEventType[];
  /**
   * **Legacy, read-only.** Where the JotForm marker lived before GAIL-1098 gave
   * Omega the typed `OmegaNodeStart.jotFormTrigger` field. Kept solely so
   * `fromOmega` can migrate a graph stored the old way.
   *
   * Do not write it: Omega refuses to publish a graph carrying this key without
   * the typed field, with `node_jot_form_trigger_metadata_only` — and the rule
   * keys on the key's *presence*, so `jotformTrigger: false` is refused too.
   */
  jotformTrigger?: boolean;
};

export type WorkflowToolStartMode = "async" | "sync";

export type StartFilesConfig = {
  description?: string;
  required?: boolean;
  maxFiles?: number;
  allowedMediaTypes?: string[];
};

// Omega API Node Types (matching OpenAPI spec)
/** Presence-only discriminator: marks a Start node as an embeddable form entrypoint. */
export type StartEmbeddedFormConfig = Record<string, never>;

/** Marks the workflow as started by a JotForm submission. `webhook.isEnabled` is a separate bit. */
export type StartJotFormTriggerConfig = {
  isEnabled: boolean;
};

/**
 * Binds this workflow to one Meta Lead Ads form; a submission on it starts a run.
 * `isEnabled` is independent of `formId` on purpose, so turning the trigger off
 * does not forget which form was picked.
 */
export type StartMetaLeadGenConfig = {
  isEnabled?: boolean;
  /** Meta's lead form id — a numeric string. Never a Page id. */
  formId?: string;
};

export type OmegaNodeStart = BaseNode & {
  type: "start";
  schedule?: ScheduleConfig;
  webhook?: WebhookConfig;
  /**
   * No Page id is stored: the notification's own `page_id` resolves the tenant,
   * so a stored copy could only agree or be wrong.
   */
  metaLeadGen?: StartMetaLeadGenConfig;
  /**
   * Note the capital F — the dead metadata key was `jotformTrigger`. A mismatch
   * is silent in both directions: Omega's containment filter matches nothing,
   * and the FE reads `undefined`.
   */
  jotFormTrigger?: StartJotFormTriggerConfig;
  inputSchema?: Record<string, unknown>;
  metadata?: StartNodeMetadata;
  files?: StartFilesConfig;
  /** When present (even as `{}`), Omega treats this workflow as an embedded form. */
  embeddedForm?: StartEmbeddedFormConfig;
};

export type OmegaNodeEnd = BaseNode & {
  type: "end";
};

export type OmegaNodeDelay = BaseNode & {
  type: "delay";
  parameters: {
    until: OmegaParameter<string>;
  };
};

export type OmegaNodeRandomNumber = BaseNode & {
  type: "random_number";
  parameters: {
    min: OmegaParameter<number>;
    max: OmegaParameter<number>;
  };
};

export type OmegaNodeSlackDirectMessage = BaseNode & {
  type: "slack_direct_message";
  parameters: {
    recipient: OmegaParameter<string>;
    message: OmegaParameter<string>;
    botToken: OmegaParameter<string>;
  };
};

export type OmegaNodeSlackPostChannelMessage = BaseNode & {
  type: "slack_post_channel_message";
  parameters: {
    channel: OmegaParameter<string>;
    message: OmegaParameter<string>;
    botToken: OmegaParameter<string>;
  };
};

export type OmegaNodeSplit = BaseNode & {
  type: "split";
  parameters: {
    branches: Array<{
      label?: string;
      check: OmegaParameterReference;
      edgeId: string;
    }>;
    defaultEdgeId: string;
  };
};

export type OmegaNodeCheck = BaseNode & {
  type: "check";
  parameters: {
    conditions: OmegaParameter<boolean>[];
    outcomes: string[];
    defaultOutcome: string;
  };
};

export type OutboundCallScriptValue = {
  name: string;
  greeting: string;
  greetingMode?: GreetingMode;
  greetingPrompt?: string;
  greetingVariants?: string[];
  greetingMorning?: string;
  greetingAfternoon?: string;
  greetingNight?: string;
  greetingMorningStart?: string;
  greetingAfternoonStart?: string;
  greetingNightStart?: string;
  objective: string;
  policy: string;
  voicemail: string;
  goodbye: string;
};

export type OmegaNodeOutboundCall = BaseNode & {
  type: "start_outbound_call";
  parameters: {
    to: OmegaParameter<string>;
    script: OmegaParameter<OutboundCallScriptValue>;
  };
};

export type OmegaNodeHttpRequest = BaseNode & {
  type: "http_request";
  parameters: {
    url: OmegaParameter<string>;
    method: OmegaParameter<string>;
    headers?: Record<string, OmegaParameter<string>>;
    queryParams?: Record<string, OmegaParameter<string>>;
    body?: OmegaParameter<string>;
  };
};

export type OmegaNodeNoop = BaseNode & {
  type: "noop";
};

export type OmegaNodeSendEmail = BaseNode & {
  type: "send_email";
  parameters: {
    to: OmegaParameter<string>;
    subject: OmegaParameter<string>;
    body: OmegaParameter<string>;
    attachmentName?: OmegaParameter<string>;
    attachmentBase64?: OmegaParameter<string>;
    htmlContent?: OmegaParameter<string> | null;
    fileAttachments?: OmegaValueList<FilePointer> | null;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeSendSms = BaseNode & {
  type: "send_sms";
  parameters: {
    to: OmegaParameter<string>;
    message: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeDataTransform = BaseNode & {
  type: "pipe";
  parameters: {
    data: Record<string, OmegaParameter<unknown>>;
  };
};

export type ContactPhoneNumberParameter = {
  number: string;
  type: string;
};

export type OmegaNodeAddContact = BaseNode & {
  type: "add_contact";
  parameters: {
    firstName: OmegaParameter<string>;
    lastName: OmegaParameter<string>;
    emails: OmegaParameter<string>[];
    phoneNumbers: OmegaParameter<ContactPhoneNumberParameter>[];
    businessName: OmegaParameter<string>;
    additionalData: Record<string, OmegaParameter<unknown>>;
  };
  onFailureEdgeId?: string;
};

/**
 * A single flat contact row consumed by the `add_bulk_contacts` node, mirroring
 * Omega's `AddBulkContacts.ContactItem` (CSV-friendly: one email, one phone).
 */
export type BulkContactItemParameter = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneType?: string;
  businessName: string;
  additionalData?: Record<string, unknown>;
};

export type OmegaNodeAddBulkContacts = BaseNode & {
  type: "add_bulk_contacts";
  parameters: {
    /**
     * Omega's `ValueList<ContactItem>` serializes two ways:
     * - a single whole-list CEL reference: `{ type: "cel", value: "node.x.data.rows" }`
     * - an array of per-item parameters: `[{ type: "literal", value: {...} }, ...]`
     *
     * `OmegaValueList` keeps the whole-list form CEL-only — a literal/template
     * object is not a valid stand-in for an entire list.
     */
    contacts: OmegaValueList<BulkContactItemParameter>;
    dedupe?: OmegaParameter<boolean>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeCreateContactList = BaseNode & {
  type: "create_contact_list";
  parameters: {
    listName: OmegaParameter<string>;
    description: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeStartCampaign = BaseNode & {
  type: "start_campaign";
  parameters: {
    campaignId: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeCreateCampaign = BaseNode & {
  type: "create_campaign";
  parameters: {
    templateCampaignId: OmegaParameter<string>;
    name?: OmegaParameter<string>;
    status?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAddContactToCampaign = BaseNode & {
  type: "add_contact_to_campaign";
  parameters: {
    campaignId: OmegaParameter<string>;
    contactId: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAddContactToContactList = BaseNode & {
  type: "add_contact_to_contact_list";
  parameters: {
    contactListId: OmegaParameter<string>;
    contactIds: OmegaValueList<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAddContactsToCampaignBulk = BaseNode & {
  type: "add_contacts_to_campaign_bulk";
  parameters: {
    campaignId: OmegaParameter<string>;
    contactIds: OmegaValueList<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeChaos = BaseNode & {
  type: "chaos";
  parameters: {
    chanceToFail: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgent = BaseNode & {
  type: "agent";
  parameters: {
    prompt: OmegaParameter<string>;
    desiredOutputFields: OmegaParameter<object>;
    agentInput?: OmegaParameter<string>;
    evalSampleRate?: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeRunSkill = BaseNode & {
  type: "run_skill";
  parameters: {
    skillId: OmegaParameter<string>;
    instruction: OmegaParameter<string>;
    files?: OmegaParameter<string>[];
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeSubGraph = BaseNode & {
  type: "sub_graph";
  description: string;
  graph: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeSubGraphStart = BaseNode & {
  type: "sub_graph_start";
};

export type OmegaNodeSubGraphEnd = BaseNode & {
  type: "sub_graph_end";
};

export type OmegaNodeForEach = BaseNode & {
  type: "foreach";
  description: string;
  parameters: {
    collection: OmegaParameter<unknown[]>;
    // Accumulate each iteration's output into `results`. Omega defaults to false
    // (omitted) to keep large fan-outs under Temporal's per-payload limit.
    collectResults?: OmegaParameter<boolean>;
  };
  graph: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeDocumentAnalysis = BaseNode & {
  type: "document_analysis";
  parameters: {
    files: OmegaParameter<string>[] | OmegaParameter<string>;
    targetDocuments: {
      documentType: string;
      description: string;
      fields?: { fieldName: string; description: string }[];
    }[];
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeJotFormSubmitForm = BaseNode & {
  type: "jotform_submit_form";
  parameters: {
    formId: OmegaParameter<string>;
    apiKey: OmegaParameter<string>;
    answers: Record<string, OmegaParameter<string>>;
  };
  onFailureEdgeId?: string;
};

/**
 * Calls one tool on a remote MCP server. Exactly one of `serverUrl` and
 * `credentialId` is set. `arguments` literals keep their JSON type: Omega stores
 * no tool schema and cannot coerce a templated "25" back to a number.
 */
export type OmegaNodeMcpCallTool = BaseNode & {
  type: "mcp_call_tool";
  parameters: {
    serverUrl?: OmegaParameter<string>;
    credentialId?: OmegaParameter<string>;
    toolName: OmegaParameter<string>;
    arguments: Record<string, OmegaParameter<unknown>>;
    headers?: Record<string, OmegaParameter<string>>;
  };
  onFailureEdgeId?: string;
};

export type FilePointer = {
  storage_path: string;
  media_type: string;
};

export type OmegaNodeSftpUploadFile = BaseNode & {
  type: "sftp_upload_file";
  parameters: {
    host: OmegaParameter<string>;
    port: OmegaParameter<number>;
    username: OmegaParameter<string>;
    remotePath: OmegaParameter<string>;
    file: OmegaParameter<FilePointer | string>;
    password?: OmegaParameter<string>;
    privateKey?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeSftpDownloadFile = BaseNode & {
  type: "sftp_download_file";
  parameters: {
    host: OmegaParameter<string>;
    port: OmegaParameter<number>;
    username: OmegaParameter<string>;
    remotePath: OmegaParameter<string>;
    password?: OmegaParameter<string>;
    privateKey?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeSftpListDirectory = BaseNode & {
  type: "sftp_list_directory";
  parameters: {
    host: OmegaParameter<string>;
    port: OmegaParameter<number>;
    username: OmegaParameter<string>;
    remotePath: OmegaParameter<string>;
    password?: OmegaParameter<string>;
    privateKey?: OmegaParameter<string>;
    namePattern?: OmegaParameter<string>;
    modifiedAfter?: OmegaParameter<string>;
    modifiedBefore?: OmegaParameter<string>;
    includeDirectories?: OmegaParameter<boolean>;
    sortBy?: OmegaParameter<string>;
    sortDescending?: OmegaParameter<boolean>;
    maxResults?: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeGoogleDriveListFiles = BaseNode & {
  type: "google_drive_list_files";
  parameters: {
    folderId?: OmegaParameter<string>;
    namePattern?: OmegaParameter<string>;
    mimeType?: OmegaParameter<string>;
    modifiedAfter?: OmegaParameter<string>;
    modifiedBefore?: OmegaParameter<string>;
    includeFolders?: OmegaParameter<boolean>;
    includeTrashed?: OmegaParameter<boolean>;
    orderBy?: OmegaParameter<string>;
    orderDescending?: OmegaParameter<boolean>;
    maxResults?: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeGoogleDriveDownloadFile = BaseNode & {
  type: "google_drive_download_file";
  parameters: {
    fileId: OmegaParameter<string>;
    exportMediaType?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeGoogleDriveUploadFile = BaseNode & {
  type: "google_drive_upload_file";
  parameters: {
    file: OmegaParameter<FilePointer | string>;
    fileName: OmegaParameter<string>;
    folderId?: OmegaParameter<string>;
    mediaType?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeGoogleSheetsAppendRows = BaseNode & {
  type: "google_sheets_append_rows";
  parameters: {
    spreadsheetId: OmegaParameter<string>;
    rows: OmegaParameter<unknown[] | null>;
    sheetName?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomSearchCustomers = BaseNode & {
  type: "agencyzoom_search_customers";
  parameters: {
    page?: OmegaParameter<number>;
    pageSize?: OmegaParameter<number>;
    sort?: OmegaParameter<string>;
    order?: OmegaParameter<string>;
    firstname?: OmegaParameter<string>;
    lastname?: OmegaParameter<string>;
    email?: OmegaParameter<string>;
    phone?: OmegaParameter<string>;
    state?: OmegaParameter<string>;
    city?: OmegaParameter<string>;
    startDate?: OmegaParameter<string>;
    endDate?: OmegaParameter<string>;
    leadSourceId?: OmegaParameter<number>;
    agentId?: OmegaParameter<number>;
    status?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomGetCustomer = BaseNode & {
  type: "agencyzoom_get_customer";
  parameters: {
    customerId: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomAddCustomerNote = BaseNode & {
  type: "agencyzoom_add_customer_note";
  parameters: {
    customerId: OmegaParameter<number>;
    note: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

/** Every filter is optional, but Omega rejects a search with none set. */
export type OmegaNodeMomentumSearchInsureds = BaseNode & {
  type: "momentum_search_insureds";
  parameters: {
    name?: OmegaParameter<string>;
    address?: OmegaParameter<string>;
    email?: OmegaParameter<string>;
    phone?: OmegaParameter<string>;
    insuredId?: OmegaParameter<string>;
    customerId?: OmegaParameter<string>;
    /** `Value<Guid>` on the wire. */
    databaseId?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeMomentumGetInsured = BaseNode & {
  type: "momentum_get_insured";
  parameters: {
    /** `Value<Guid>` on the wire. */
    insuredDatabaseId: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeMomentumAddNote = BaseNode & {
  type: "momentum_add_note";
  parameters: {
    /** `Value<Guid>` on the wire. */
    insuredDatabaseId: OmegaParameter<string>;
    /** The note text. */
    subject: OmegaParameter<string>;
    /** A note type name. Omitted means Momentum's default. */
    type?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomSearchLeads = BaseNode & {
  type: "agencyzoom_search_leads";
  parameters: {
    page?: OmegaParameter<number>;
    pageSize?: OmegaParameter<number>;
    sort?: OmegaParameter<string>;
    order?: OmegaParameter<string>;
    status?: OmegaParameter<string>;
    assignedTo?: OmegaParameter<number>;
    leadSourceId?: OmegaParameter<number>;
    customerName?: OmegaParameter<string>;
    customerEmail?: OmegaParameter<string>;
    customerPhone?: OmegaParameter<string>;
    startDate?: OmegaParameter<string>;
    endDate?: OmegaParameter<string>;
    soldPeriod?: OmegaParameter<string>;
    workflowId?: OmegaParameter<number>;
    workflowStageId?: OmegaParameter<string>;
    lastActivityEarliestDate?: OmegaParameter<string>;
    lastActivityLatestDate?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomGetLead = BaseNode & {
  type: "agencyzoom_get_lead";
  parameters: {
    leadId: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomAddLeadNote = BaseNode & {
  type: "agencyzoom_add_lead_note";
  parameters: {
    leadId: OmegaParameter<number>;
    note: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomSearchTasks = BaseNode & {
  type: "agencyzoom_search_tasks";
  parameters: {
    page?: OmegaParameter<number>;
    pageSize?: OmegaParameter<number>;
    sort?: OmegaParameter<string>;
    order?: OmegaParameter<string>;
    type?: OmegaParameter<string>;
    status?: OmegaParameter<string>;
    assigneeId?: OmegaParameter<number>;
    period?: OmegaParameter<string>;
    startDate?: OmegaParameter<string>;
    endDate?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomCompleteTask = BaseNode & {
  type: "agencyzoom_complete_task";
  parameters: {
    taskId: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomCreateTask = BaseNode & {
  type: "agencyzoom_create_task";
  parameters: {
    title: OmegaParameter<string>;
    dueDatetime?: OmegaParameter<string>;
    comments?: OmegaParameter<string>;
    assigneeId?: OmegaParameter<number>;
    contactEmail?: OmegaParameter<string>;
    customerId?: OmegaParameter<number>;
    leadId?: OmegaParameter<number>;
    duration?: OmegaParameter<number>;
    type?: OmegaParameter<string>;
    invitees?: OmegaValueList<string>;
    timeSpecific?: OmegaParameter<boolean>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomUpdateTask = BaseNode & {
  type: "agencyzoom_update_task";
  parameters: {
    taskId: OmegaParameter<number>;
    title?: OmegaParameter<string>;
    dueDatetime?: OmegaParameter<string>;
    comments?: OmegaParameter<string>;
    assigneeId?: OmegaParameter<number>;
    contactEmail?: OmegaParameter<string>;
    customerId?: OmegaParameter<number>;
    leadId?: OmegaParameter<number>;
    duration?: OmegaParameter<number>;
    type?: OmegaParameter<string>;
    invitees?: OmegaValueList<string>;
    timeSpecific?: OmegaParameter<boolean>;
    status?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

/**
 * One custom-field assignment. `fieldName` is an AgencyZoom slug (e.g. `cf1`);
 * `fieldValue` is always an array, even for a single value.
 */
export type AgencyZoomCustomFieldParameter = {
  fieldName: string;
  fieldValue: string[];
};

/**
 * Parameters shared by both create-lead tiles, all optional: blank strings, empty
 * lists and ids that are unset or `0` are omitted. Each tile declares its own
 * required fields below.
 */
type AgencyZoomLeadSharedParameters = {
  email?: OmegaParameter<string>;
  country?: OmegaParameter<string>;
  leadSourceId?: OmegaParameter<number>;
  assignTo?: OmegaParameter<number>;
  lastname?: OmegaParameter<string>;
  middlename?: OmegaParameter<string>;
  nickname?: OmegaParameter<string>;
  phone?: OmegaParameter<string>;
  secondaryEmail?: OmegaParameter<string>;
  secondaryPhone?: OmegaParameter<string>;
  streetAddress?: OmegaParameter<string>;
  streetAddressLine2?: OmegaParameter<string>;
  city?: OmegaParameter<string>;
  state?: OmegaParameter<string>;
  zip?: OmegaParameter<string>;
  birthday?: OmegaParameter<string>;
  notes?: OmegaParameter<string>;
  agencyNumber?: OmegaParameter<string>;
  departmentCode?: OmegaParameter<string>;
  groupCode?: OmegaParameter<string>;
  /** One string; multiple tags are separated by `;`. */
  tagNames?: OmegaParameter<string>;
  contactDate?: OmegaParameter<string>;
  soldDate?: OmegaParameter<string>;
  quoteDate?: OmegaParameter<string>;
  xDate?: OmegaParameter<string>;
  nextExpirationDate?: OmegaParameter<string>;
  /** snake_case on the wire (e.g. `domestic_partner`). */
  maritalStatus?: OmegaParameter<string>;
  csrId?: OmegaParameter<number>;
  assignmentGroupId?: OmegaParameter<number>;
  otherProducers?: OmegaValueList<number>;
  otherCsrs?: OmegaValueList<number>;
  customFields?: OmegaValueList<AgencyZoomCustomFieldParameter>;
};

export type OmegaNodeAgencyZoomCreateLead = BaseNode & {
  type: "agencyzoom_create_lead";
  parameters: AgencyZoomLeadSharedParameters & {
    firstname: OmegaParameter<string>;
    pipelineId: OmegaParameter<number>;
    stageId: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeAgencyZoomCreateBusinessLead = BaseNode & {
  type: "agencyzoom_create_business_lead";
  parameters: AgencyZoomLeadSharedParameters & {
    pipelineId: OmegaParameter<number>;
    stageId: OmegaParameter<number>;
    /** Omega enforces a one-of over `firstname` and `name`, so neither alone is required. */
    firstname?: OmegaParameter<string>;
    name?: OmegaParameter<string>;
    contactName?: OmegaParameter<string>;
    dba?: OmegaParameter<string>;
    fein?: OmegaParameter<string>;
    /** Free text upstream — no enum. */
    businessEntity?: OmegaParameter<string>;
    businessClassification?: OmegaParameter<string>;
    businessClassificationId?: OmegaParameter<number>;
    yearBusinessStarted?: OmegaParameter<number>;
    /** `0` is a real value for the count and the two amounts below, unlike an id. */
    numberOfEmployees?: OmegaParameter<number>;
    annualRevenue?: OmegaParameter<number>;
    payroll?: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeCreateFile = BaseNode & {
  type: "create_file";
  parameters: {
    content: OmegaParameter<string>;
    fileName: OmegaParameter<string>;
    mediaType: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

/** Wire parameters shared by the row-parsing file nodes (parse_csv, parse_excel). */
export type OmegaParseRowsParameters = {
  file: OmegaParameter<FilePointer>;
  hasHeader: OmegaParameter<boolean>;
  columns?: OmegaValueList<string>;
  skipRows?: OmegaParameter<number>;
  maxRows?: OmegaParameter<number>;
  filter?: OmegaParameter<string>;
};

export type OmegaNodeParseCsv = BaseNode & {
  type: "parse_csv";
  parameters: OmegaParseRowsParameters;
  onFailureEdgeId?: string;
};

/** parse_excel adds worksheet selection and cell value handling to the shared parameters. */
export type OmegaParseExcelParameters = OmegaParseRowsParameters & {
  sheet?: OmegaParameter<string>;
  startRow?: OmegaParameter<number>;
  cellValues?: OmegaParameter<"text" | "typed">;
};

export type OmegaNodeParseExcel = BaseNode & {
  type: "parse_excel";
  parameters: OmegaParseExcelParameters;
  onFailureEdgeId?: string;
};

export type OmegaNodeSliceCsv = BaseNode & {
  type: "slice_csv";
  parameters: {
    file: OmegaParameter<FilePointer>;
    hasHeader: OmegaParameter<boolean>;
    maxRows?: OmegaParameter<number>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeCreateCsv = BaseNode & {
  type: "create_csv";
  parameters: {
    rows: OmegaParameter<unknown[]>;
    fileName: OmegaParameter<string>;
    includeHeader?: OmegaParameter<boolean>;
    fields?: OmegaValueList<string>;
    headers?: OmegaValueList<string>;
    skipRows?: OmegaParameter<number>;
    maxRows?: OmegaParameter<number>;
    filter?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeGetCallData = BaseNode & {
  type: "get_call_data";
  parameters: {
    callSid: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeDownloadCallRecording = BaseNode & {
  type: "download_call_recording";
  parameters: {
    callSid: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

export type OmegaNodeWebSearch = BaseNode & {
  type: "web_search";
  parameters: {
    query: OmegaParameter<string>;
    limit?: OmegaParameter<number>;
    contentFormat?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

/**
 * WebResource wire format.
 *
 * The C# record is `(string Url, string? Title, string? Description, string? Content)`
 * with no default values. The backend runs System.Text.Json with
 * `RespectRequiredConstructorParameters = true`, so all four properties must be
 * present in the JSON — nullable ones require an explicit `null`, not omission.
 * That's why title/description/content are `string | null` (always present, may
 * be null) rather than `string | undefined` (optional, may be omitted).
 */
export type WebResource = {
  url: string;
  title: string | null;
  description: string | null;
  content: string | null;
};

/**
 * Canonical empty WebResource. Every nullable field must be present (set to
 * null) because the backend's System.Text.Json deserializer is configured with
 * `RespectRequiredConstructorParameters = true` and will reject payloads that
 * omit a required (even nullable) record parameter.
 */
export function createEmptyWebResource(url = ""): WebResource {
  return { url, title: null, description: null, content: null };
}

export type OmegaNodeWebExtract = BaseNode & {
  type: "web_extract";
  parameters: {
    sources: OmegaValueList<WebResource>;
    prompt?: OmegaParameter<string>;
    schema?: OmegaParameter<string>;
  };
  onFailureEdgeId?: string;
};

/**
 * Fill PDF Form node — mirrors Omega's `FillPdfForm` (fill_pdf_form).
 *
 * - `template` is a FilePointer to an AcroForm-fillable PDF (forced to CEL in
 *   the UI; must come from an upstream node like a Start file upload, a
 *   Drive/SFTP download, or another node's `data.file` — Create Text File
 *   outputs plain text and is not a valid PDF template).
 * - `sourceData` is the upstream payload to map onto the form. The wire type
 *   is `Value<object>`, so this is always a CEL reference to an upstream
 *   node's data (`unknown` here because the resolved shape depends on the
 *   producer).
 * - `hints` is an optional free-form cheat sheet for the LLM mapping step.
 * - `flatten` bakes the filled values into the page content streams when
 *   true; off by default.
 */
export type OmegaNodeFillPdfForm = BaseNode & {
  type: "fill_pdf_form";
  parameters: {
    // `OmegaParameter<T>` only constrains the literal branch — CEL/template
    // wrappers carry a string regardless. `template` is `Value<FilePointer>`
    // on the wire (mirrors `OmegaNodeParseCsv.parameters.file`), so a literal
    // string here would be a schema violation. The transformer coerces any
    // non-object literal to an empty FilePointer sentinel.
    template: OmegaParameter<FilePointer>;
    sourceData: OmegaParameter<unknown>;
    fileName: OmegaParameter<string>;
    hints?: OmegaParameter<string>;
    flatten?: OmegaParameter<boolean>;
  };
  onFailureEdgeId?: string;
};

/**
 * Map node — mirrors Omega's `Map` (map): applies a projection to each item
 * of a list and emits the projected list. A foreach + per-item data transform
 * in a single node, executed as one activity (no inner graph).
 *
 * - `items` resolves against the full workflow context (usually a CEL
 *   reference to an upstream array, e.g. `node.parse_csv.data.rows`).
 * - `projection` maps output field name → expression evaluated once per item
 *   with ONLY `node.item` and `node.index` in scope. Expressions referencing
 *   other nodes or `config.*`/`sys.*`/`secret.*` are rejected by Omega at
 *   publish time. When omitted or empty, items pass through unchanged.
 */
export type OmegaNodeMap = BaseNode & {
  type: "map";
  parameters: {
    items: OmegaParameter<unknown[]>;
    projection?: Record<string, OmegaParameter<unknown>>;
  };
  onFailureEdgeId?: string;
};

// Union type for all Omega nodes
export type WorkflowNode =
  | OmegaNodeStart
  | OmegaNodeEnd
  | OmegaNodeDelay
  | OmegaNodeRandomNumber
  | OmegaNodeSlackDirectMessage
  | OmegaNodeSlackPostChannelMessage
  | OmegaNodeSplit
  | OmegaNodeCheck
  | OmegaNodeOutboundCall
  | OmegaNodeHttpRequest
  | OmegaNodeSendEmail
  | OmegaNodeSendSms
  | OmegaNodeDataTransform
  | OmegaNodeAddContact
  | OmegaNodeAddBulkContacts
  | OmegaNodeCreateContactList
  | OmegaNodeNoop
  | OmegaNodeStartCampaign
  | OmegaNodeCreateCampaign
  | OmegaNodeAddContactToCampaign
  | OmegaNodeAddContactToContactList
  | OmegaNodeAddContactsToCampaignBulk
  | OmegaNodeChaos
  | OmegaNodeAgent
  | OmegaNodeRunSkill
  | OmegaNodeSubGraph
  | OmegaNodeSubGraphStart
  | OmegaNodeSubGraphEnd
  | OmegaNodeForEach
  | OmegaNodeDocumentAnalysis
  | OmegaNodeJotFormSubmitForm
  | OmegaNodeMcpCallTool
  | OmegaNodeSftpUploadFile
  | OmegaNodeSftpDownloadFile
  | OmegaNodeSftpListDirectory
  | OmegaNodeGoogleDriveListFiles
  | OmegaNodeGoogleDriveDownloadFile
  | OmegaNodeGoogleDriveUploadFile
  | OmegaNodeGoogleSheetsAppendRows
  | OmegaNodeAgencyZoomSearchCustomers
  | OmegaNodeAgencyZoomGetCustomer
  | OmegaNodeAgencyZoomAddCustomerNote
  | OmegaNodeAgencyZoomSearchLeads
  | OmegaNodeAgencyZoomGetLead
  | OmegaNodeAgencyZoomAddLeadNote
  | OmegaNodeAgencyZoomSearchTasks
  | OmegaNodeAgencyZoomCompleteTask
  | OmegaNodeAgencyZoomCreateTask
  | OmegaNodeAgencyZoomUpdateTask
  | OmegaNodeAgencyZoomCreateLead
  | OmegaNodeAgencyZoomCreateBusinessLead
  | OmegaNodeMomentumSearchInsureds
  | OmegaNodeMomentumGetInsured
  | OmegaNodeMomentumAddNote
  | OmegaNodeCreateFile
  | OmegaNodeParseCsv
  | OmegaNodeParseExcel
  | OmegaNodeSliceCsv
  | OmegaNodeCreateCsv
  | OmegaNodeGetCallData
  | OmegaNodeDownloadCallRecording
  | OmegaNodeWebSearch
  | OmegaNodeWebExtract
  | OmegaNodeFillPdfForm
  | OmegaNodeMap;

export type OmegaContainerNode = OmegaNodeSubGraph | OmegaNodeForEach;

export function isContainerOmegaNode(node: WorkflowNode): node is OmegaContainerNode {
  return node.type === "sub_graph" || node.type === "foreach";
}

// ============================================================================
// Workflow Run Types
// ============================================================================

/**
 * Event types that can occur during a workflow run
 */
type WorkflowRunEventType =
  | "workflow_started"
  | "workflow_completed"
  | "workflow_cancelled"
  | "workflow_failed"
  | "workflow_timed_out"
  | "node_started"
  | "node_completed"
  | "node_delay_started"
  | "node_delay_completed"
  | "node_delay_failed"
  | "node_expression_evaluation_failed"
  | "sub_graph_started"
  | "sub_graph_completed"
  | "foreach_started"
  | "foreach_completed"
  | "foreach_iteration_started"
  | "foreach_iteration_completed";

/**
 * A single event that occurred during a workflow run.
 * Events are ordered by stepIndex and unique per runId.
 * - For node_started: content contains the node input
 * - For node_completed: content contains the node output
 */
export type WorkflowRunEvent = {
  workflowId: string;
  organizationId: string;
  runId: string;
  type: WorkflowRunEventType;
  stepIndex: number;
  timestamp: string;
  content: Record<string, unknown>;
  /**
   * Runner-reported success flag for the event. Optional for backwards
   * compatibility with older payloads, but always present on current Omega
   * traces.
   */
  isSuccess?: boolean;
  nodeId: string | null;
  nodeName?: string | null;
  parentNodeId?: string | null;
  parentNodeName?: string | null;
};

/**
 * Status of a workflow run derived from its events
 */
export type WorkflowRunStatus = "running" | "completed" | "failed" | "cancelled";

/**
 * A workflow run with its events grouped together.
 * Derived from WorkflowRunEvent[] by grouping on runId.
 */
export type WorkflowRun = {
  runId: string;
  workflowId: string;
  workflowName?: string;
  status: WorkflowRunStatus;
  startedAt: string;
  completedAt?: string;
  events: WorkflowRunEvent[];
};

// ============================================================================
// Workflow Run Progress Types (Omega real-time polling API)
// ============================================================================

/**
 * Trace types returned by the Omega real-time progress API.
 */
export type WorkflowRunTraceType =
  | "workflow_started"
  | "workflow_completed"
  | "workflow_cancelled"
  | "workflow_failed"
  | "workflow_timed_out"
  | "node_started"
  | "node_completed"
  | "node_delay_started"
  | "node_delay_completed"
  | "node_delay_failed"
  | "node_expression_evaluation_failed"
  | "sub_graph_started"
  | "sub_graph_completed"
  | "foreach_started"
  | "foreach_completed"
  | "foreach_iteration_started"
  | "foreach_iteration_completed";

/**
 * A single trace event from the Omega real-time progress API.
 * Ordered by stepIndex within a run.
 */
export type WorkflowRunTrace = {
  workflowId: string;
  organizationId: string;
  runId: string;
  type: WorkflowRunTraceType;
  stepIndex: number;
  timestamp: string;
  content: unknown;
  isSuccess: boolean;
  nodeId: string | null;
  nodeName: string | null;
  parentNodeId?: string | null;
  parentNodeName?: string | null;
};

/**
 * Visual status for a node during run progress animation / debug pause.
 */
export type NodeRunStatus =
  | "running"
  | "paused"
  | "completed"
  | "failed"
  | "cancelled";

// ============================================================================
// Node Testing Types
// ============================================================================

/**
 * Node definition returned from the API
 * Contains JSON Schema format for a node type's input/output/resolved
 */
export interface NodeDefinition {
  type: string;
  input: unknown;
  output: NodeSchemaDefinition;
  resolved: NodeSchemaDefinition;
}

/**
 * JSON Schema definition for node properties (used by resolved and output)
 */
export interface NodeSchemaDefinition {
  type: string | string[];
  properties?: Record<string, NodePropertySchema>;
  required?: string[];
}

/**
 * Schema for individual node property.
 * The backend sends a full JSON Schema object; these are the fields we use.
 */
export interface NodePropertySchema {
  type?: string | string[];
  default?: unknown;
  additionalProperties?: unknown;
  items?: NodePropertySchema;
  properties?: Record<string, NodePropertySchema>;
  required?: string[];
  enum?: unknown[];
}

/**
 * One callable signature of a CEL function. `input`/`output` are standard JSON
 * Schema documents (the input describes the positional argument tuple via
 * `prefixItems`); we only consume `example` directly in the UI.
 */
export interface CelFunctionOverload {
  input: unknown;
  output: unknown;
  example?: string;
}

/**
 * How a function is written at a call site, from Omega's `callStyle`. Member
 * style folds the receiver in as the first parameter (`value.replace(old, new)`);
 * global style takes every parameter in the parens.
 */
export type CelCallStyle = "member" | "global";

/**
 * FE-facing metadata for a CEL function callable inside expressions, as returned
 * by Omega's `GET /expressions/functions` endpoint.
 */
export interface CelFunctionDefinition {
  name: string;
  category: string;
  description: string;
  isBuiltIn: boolean;
  /**
   * Optional so a stale Omega deploy that omits the field still type-checks;
   * `isMethodStyle` falls back to a category heuristic when it is missing.
   */
  callStyle?: CelCallStyle;
  overloads: CelFunctionOverload[];
}

/**
 * Node provider credential returned by Omega (for integration-backed nodes).
 */
export interface NodeProviderCredential {
  id: string;
  label: string;
  scopes: string[];
  status: string;
  createdAt: string;
}

/**
 * Represents the different shapes a parameter value can take in a test node payload
 */
export type TestNodeParameterValue = 
  | { type: "literal"; value: unknown }                      // Simple parameters
  | { type: "literal"; value: unknown }[]                    // Value<T>[] array parameters
  | Record<string, { type: "literal"; value: unknown }>      // Map-like parameters
  | Record<string, unknown>[];                              // Plain domain object arrays (e.g. targetDocuments)

/**
 * Payload structure for testing a single node
 */
export interface TestNodePayload {
  type: string;
  id: string;
  name: string;
  parameters: Record<string, TestNodeParameterValue>;
}

/**
 * Response from testing a node
 */
export interface TestNodeResponse {
  result?: unknown;
  error?: string;
}

export type ScheduledWorkflowSummary = {
  id: string;
  name: string;
};

/**
 * Result of scanning published workflows for schedules. `incomplete` means at
 * least one page or graph could not be read, so an empty list does not prove
 * that nothing is scheduled.
 */
export type ScheduledWorkflowScan = {
  workflows: ScheduledWorkflowSummary[];
  incomplete: boolean;
};

/**
 * How an `ExpressionInput` interprets its value: a plain literal, a mention
 * template, or a CEL expression.
 */
export type ExpressionInputMode = "static" | "template" | "cel";
